import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  feedbackTypeFormSchema,
  type FeedbackType,
  type FeedbackTypeFormValues,
  type CreateFeedbackTypePayload,
  type UpdateFeedbackTypePayload,
} from '../types/feedbackType.types';
import { useCreateFeedbackType } from './useCreateFeedbackType';
import { useUpdateFeedbackType } from './useUpdateFeedbackType';

interface UseFeedbackTypeFormParams {
  feedbackType: FeedbackType | null;
  open: boolean;
  onClose: () => void;
}

const EMPTY_DEFAULTS: FeedbackTypeFormValues = {
  code: '',
  name: '',
  description: '',
  isActive: true,
};

function toFormValues(feedbackType: FeedbackType | null): FeedbackTypeFormValues {
  if (!feedbackType) return EMPTY_DEFAULTS;
  return {
    code: feedbackType.code,
    name: feedbackType.name,
    description: feedbackType.description ?? '',
    isActive: feedbackType.isActive,
  };
}

function buildCreatePayload(values: FeedbackTypeFormValues): CreateFeedbackTypePayload {
  const trimmedDescription = values.description.trim();
  const payload: CreateFeedbackTypePayload = {
    code: values.code,
    name: values.name,
    isActive: values.isActive,
  };
  if (trimmedDescription.length > 0) {
    payload.description = trimmedDescription;
  }
  return payload;
}

export function useFeedbackTypeForm({ feedbackType, open, onClose }: UseFeedbackTypeFormParams) {
  const isEditMode = feedbackType !== null;
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);

  const form = useForm<FeedbackTypeFormValues>({
    resolver: zodResolver(feedbackTypeFormSchema),
    defaultValues: toFormValues(feedbackType),
  });

  useEffect(() => {
    if (open) {
      form.reset(toFormValues(feedbackType));
    } else {
      form.reset(EMPTY_DEFAULTS);
    }
  }, [feedbackType, open, form]);

  const createMutation = useCreateFeedbackType(onClose);
  const updateMutation = useUpdateFeedbackType(onClose);

  const isPending = isEditMode ? updateMutation.isPending : createMutation.isPending;

  function submit(values: FeedbackTypeFormValues) {
    if (isEditMode && feedbackType) {
      const changedKeys = Object.keys(
        form.formState.dirtyFields,
      ) as (keyof FeedbackTypeFormValues)[];
      if (changedKeys.length === 0) {
        onClose();
        return;
      }
      const updatePayload: UpdateFeedbackTypePayload = Object.fromEntries(
        changedKeys.map((key) => [key, values[key]]),
      );
      updateMutation.mutate({ id: feedbackType.id, payload: updatePayload });
      return;
    }
    createMutation.mutate(buildCreatePayload(values));
  }

  function requestClose() {
    if (!form.formState.isDirty) {
      onClose();
      return;
    }
    setIsDiscardDialogOpen(true);
  }

  function confirmDiscard() {
    setIsDiscardDialogOpen(false);
    onClose();
  }

  function cancelDiscard() {
    setIsDiscardDialogOpen(false);
  }

  return {
    form,
    isEditMode,
    isPending,
    submit,
    isDiscardDialogOpen,
    requestClose,
    confirmDiscard,
    cancelDiscard,
  };
}
